// noinspection GraphQLUnresolvedReference

import React, {createContext, useContext, useEffect, useState} from 'react'
import {ApolloClient, ApolloProvider, createHttpLink, gql, InMemoryCache, useQuery} from '@apollo/client'
import {setContext} from '@apollo/client/link/context'
import {relayStylePagination} from '@apollo/client/utilities'

const httpLink = createHttpLink({
  uri: 'https://shop.stringtheory.us/api/2026-04/graphql.json',
})

const authLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      'X-Shopify-Storefront-Access-Token': '0a807bbc4b18aa4d6c4288b54eca178e',
    }
  }
})

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          products: relayStylePagination(['query', 'sortBy', 'reverse']),
        },
      },
    },
  }),
})

export default function ShopifyProvider({children}) {
  return <ApolloProvider client={client}>
    <CartProvider>{children}</CartProvider>
  </ApolloProvider>
}

const CartContext = createContext({
  cart: null,
  loading: false,
  open: false,
  toggleCart: () => {},
  updateCart: () => {},
})

export const useCart = () => useContext(CartContext)

export const CartConsumer = CartContext.Consumer

function CartProvider({children}) {
  const [cartId, setCartId] = useState(sessionStorage.getItem('cart'))
  const [cartOpen, setCartOpen] = useState(false)
  useEffect(() => sessionStorage.setItem('cart', cartId), [cartId])
  const {loading, error, data} = useQuery(FETCH_CART, {
    variables: {cartId: cartId},
    skip: !cartId || cartId === 'null',
  })
  
  if (error) console.log(`Failed to fetch cart: ${error}`)
  
  return <CartContext.Provider value={{
    cart: !data ? null : flattenData(data.cart),
    loading: loading,
    open: cartOpen,
    toggleCart: (value) => setCartOpen(value),
    updateCart: (value) => setCartId(!value ? null : value.id),
  }}>{children}</CartContext.Provider>
}

export function createLineItem(variant, quantity, extras) {
  return {
    attributes: Object.keys(extras || {}).map(k => {
      return {key: k, value: extras[k]}
    }),
    quantity: quantity || 1,
    merchandiseId: variant,
  }
}

export function flattenData(data) {
  if (!data || typeof data !== 'object') return data
  if (Array.isArray(data)) {
    return data.map(item => flattenData(item))
  }
  return Object.keys(data).reduce((acc, key) => {
    const value = data[key]
    if (value && typeof value === 'object') {
      if (value.edges) {
        acc[key] = value.edges.map(edge => flattenData(edge.node))
      } else if (value.node) {
        acc[key] = flattenData(value.node)
      } else {
        acc[key] = flattenData(value)
      }
    } else {
      acc[key] = value
    }
    return acc
  }, {})
}

export function resizeImage(url, desiredSize) {
  const reversedSrc = (url || '').split('').reverse().join('')
  const reversedSize = `_${desiredSize}x`.split('').reverse().join('')
  const replaced = reversedSrc.replace(/x\d+_/i, reversedSize)
  return replaced.split('').reverse().join('')
}

export function groupLineItems(lineItems) {
  const lineItemMap = (lineItems || [])
    .map(item => {
      return {
        key: ((item.attributes || []).find(attr => attr.key === 'Design ID') || {value: 'single'}).value,
        value: item,
      }
    })
    .reduce((acc, curr) => {
      return {
        ...acc,
        [curr.key]: (acc[curr.key] || []).concat([curr.value])
      }
    }, {})
  return Object.keys(lineItemMap).map(k => {
    if (k === 'single') return lineItemMap[k].map(item => [item])
    return [lineItemMap[k]]
  }).reduce((acc, curr) => {
    return acc.concat(curr)
  }, [])
}

const ProductFragment = gql`
  fragment ProductFragment on Product {
    availableForSale
    compareAtPriceRange {
      minVariantPrice {
        amount
      }
    }
    descriptionHtml
    handle
    id
    images(first: 10) {
      edges {
        node {
          url(transform: {
            maxWidth: 480,
            preferredContentType: JPG
          })
        }
      }
    }
    metafields(identifiers: [
      {key: "orderCount", namespace: "firebase"},
      {key: "reviewCount", namespace: "firebase"},
      {key: "reviewTotal", namespace: "firebase"},
    ]) {
      id
      key
      value
    }
    priceRange {
      minVariantPrice {
        amount
      }
      maxVariantPrice {
        amount
      }
    }
    tags
    title
    variants(first: 100) {
      edges {
        node {
          id
          compareAtPrice {
            amount
          }
          image {
            url(transform:{
              maxWidth: 480,
              preferredContentType: JPG
            })
          }
          price {
            amount
          }
          sku
          title
        }
      }
    }
    vendor
  }
`

export const FETCH_MATERIALS = gql`
  query {
    products(query: "tag:material", first: 100) {
      edges {
        node {
          ...ProductFragment
        }
      }
    }
  }
  ${ProductFragment}
`

export const FETCH_THEORISTS = gql`
  query {
    products(query: "tag:theorist", first: 100) {
      edges {
        node {
          ...ProductFragment
        }
      }
    }
  }
  ${ProductFragment}
`

export const FETCH_MODULE = gql`
  query FetchModule($query: String!, $sortBy: ProductSortKeys!, $reverse: Boolean!, $first: Int = 100, $after: String) {
    products(query: $query, sortKey: $sortBy, reverse: $reverse, first: $first, after: $after) {
      edges {
        node {
          ...ProductFragment
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
  ${ProductFragment}
`

export const FETCH_PRODUCT = gql`
  query FetchProduct($productId: ID!) {
    node(id: $productId) {
      ...ProductFragment
    }
  }
  ${ProductFragment}
`

export const FETCH_SERVICES = gql`
  query {
    collections(first: 3) {
      edges {
        node {
          descriptionHtml
          handle
          id
          image {
            url(transform: {
              maxHeight: 128,
              maxWidth: 128,
              scale: 3
            })
          }
          products(first: 10) {
            edges {
              node {
                descriptionHtml
                id
                images(first: 10) {
                  edges {
                    node {
                      url(transform: {
                        maxWidth: 560,
                        maxHeight: 560,
                        scale: 3
                      })
                    }
                  }
                }
                priceRange {
                  minVariantPrice {
                    amount
                  }
                }
                tags
                title
                variants(first: 10) {
                  edges {
                    node {
                      id
                      image {
                        url(transform: {
                          maxWidth: 560,
                          maxHeight: 560,
                          scale: 3
                        })
                      }
                      price {
                        amount
                      }
                      sku
                      title              
                    }
                  }
                }
                vendor
              }
            }
          }
          title
        }
      }
    }
    products(query: "tag:restring tag:service", first: 1) {
      edges {
        node {
          variants(first: 3) {
            edges {
              node {
                id
                price {
                  amount
                }
                sku
                title              
              }
            }
          }
        }
      }
    }
  }
`

const CartFragment = gql`
  fragment CartFragment on Cart {
    id
    checkoutUrl
    lines(first: 100) {
      edges {
        node {
          id
          quantity
          attributes {
            key
            value
          }
          merchandise {
            ... on ProductVariant {
              id
              title
              compareAtPrice {
                amount
              }
              image {
                url(transform: {
                  maxHeight: 128,
                  maxWidth: 128,
                  scale: 3
                })
              }
              price {
                amount
              }
              product {
                handle
                tags
                title
              }
              sku
            }
          }
        }
      }
    }
    note
    cost {
      subtotalAmount {
        amount
      }
      totalAmount {
        amount
      }
      totalTaxAmount {
        amount
      }
    }
    discountCodes {
      code
      applicable
    }
  }
`

export const FETCH_CART = gql`
  query FetchCart($cartId: ID!) {
    cart(id: $cartId) {
      ...CartFragment
    }
  }
  ${CartFragment}
`

export const CREATE_CART = gql`
  mutation cartCreate($cartInput: CartInput!) {
    cartCreate(input: $cartInput) {
      cart {
        ...CartFragment
      }
      userErrors {
        message
        field
      }
    }
  }
  ${CartFragment}
`

export const CART_LINES_ADD = gql`
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        ...CartFragment
      }
      userErrors {
        message
        field
      }
    }
  }
  ${CartFragment}
`

export const CART_LINES_UPDATE = gql`
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        ...CartFragment
      }
      userErrors {
        message
        field
      }
    }
  }
  ${CartFragment}
`

export const CART_LINES_REMOVE = gql`
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        ...CartFragment
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CartFragment}
`

export const CART_ATTRIBUTES_UPDATE = gql`
  mutation cartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
    cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
      cart {
        ...CartFragment
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CartFragment}
`

export const CART_DISCOUNT_CODES_UPDATE = gql`
  mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        ...CartFragment
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CartFragment}
`

export const CART_BUYER_IDENTITY_UPDATE = gql`
  mutation cartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
    cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
      cart {
        ...CartFragment
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CartFragment}
`
